import { apiClient } from "@/api/api-client";
import { Tiptap } from "@/components/common/tiptap/Tiptap";
import { Button } from "@/components/core/buttons/button";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import TagsInput from "@/components/core/forms/tags-input";
import { Textarea } from "@/components/core/forms/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { helpTopicQueryOptions } from "@/features/dashboard/api/queries/help-topic-queries";
import helpCategoryService from "@/features/dashboard/api/services/help-category-service";
import roleService from "@/features/dashboard/api/services/role-service";
import type { HelpCategory } from "@/features/dashboard/types/help-category";
import { type HelpTopicFormSchema, helpTopicFormSchema } from "@/features/dashboard/types/help-topic";
import type { Role } from "@/features/dashboard/types/role";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/help-topics_/$topicId")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(helpTopicQueryOptions(params.topicId));
	},
	component: RouteComponent,
});

function RouteComponent() {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [videoFile, setVideoFile] = useState<{
		original_url?: string;
		mime_type?: string;
	} | null>(null);
	const params = Route.useParams();
	const helpTopicQuery = useSuspenseQuery(helpTopicQueryOptions(params.topicId));
	const data = helpTopicQuery.data;

	const form = useForm<HelpTopicFormSchema>({
		resolver: zodResolver(helpTopicFormSchema),
		defaultValues: {
			id: params.topicId,
			title: data.title || "",
			body: data.body || "",
			description: data.description || "",
			category: data.category ? { value: data.category.value, label: data.category.label } : null,
			roles: data?.roles ? data?.roles : [],
		},
	});

	const loadOptions = async (value: string) => {
		const items = await helpCategoryService.list({
			"filter[search]": value,
		});

		return items.data.map((item: HelpCategory) => ({
			value: item.id,
			label: item.name,
		}));
	};

	const loadRoleOptions = async (value: string) => {
		const items = await roleService.list({
			"filter[search]": value,
		});

		return items.map((item: Role) => ({
			value: item.id,
			label: item.name,
		}));
	};

	const handleSetFile = (file: any) => {
		console;
		if (file) {
			form.setValue("new_attachment", URL.createObjectURL(file));
			setVideoFile(file);
		} else {
			setVideoFile(null);
			form.setValue("new_attachment", undefined);
		}
	};

	const handleRemoveFile = () => {
		console.log("remove file");
		setVideoFile(null);
		form.setValue("attachment", undefined);
		form.setValue("new_attachment", undefined);
		//add code for removing media
	};

	const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		console.log("onFileChange");
		form.setValue("new_attachment", null);
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files?.[0] || null;
			setVideoFile(URL.createObjectURL(file));
			if (file) {
				form.setValue("new_attachment", file);
			} else {
				form.setValue("new_attachment", undefined);
				setVideoFile(null);
			}
		}
	};

	const handleSubmit = async (data: HelpTopicFormSchema) => {
		setIsLoading(true);
		console.log(data);
		const formData = new FormData();
		Object.keys(data).forEach((key) => {
			const typedKey = key as keyof HelpTopicFormSchema;
			if (typedKey === "category") {
				formData.append("help_category_id", data.category ? data.category.value : "");
			} else if (data[typedKey] instanceof File) {
				formData.append(key, data[typedKey] as File);
			} else if (typedKey === "roles") {
				data[typedKey].forEach((item: any) => {
					formData.append("roles[]", item.value);
				});
			} else {
				formData.append(key, data[typedKey] as string);
			}
		});

		try {
			const response = await apiClient.post(`/help-topics/${data.id}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			if (response) {
				toast.success("Help Topic successfully updated.");
				window.location.href = "/help-topics";
			}
		} catch (error) {
			console.log(error);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (data && data.attachment && data.attachment.original_url) {
			setVideoFile(data.attachment.original_url);
		}
	}, [data]);

	return (
		<Content pageName="Help Categories">
			{/*<Header title="Help Categories" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<Form {...form}>
						<form className="flex flex-col w-full max-w-base p-6 gap-6" onSubmit={(e) => e.preventDefault()}>
							<div className="flex flex-row items-center justify-between w-full gap-4">
								<div className="inline-flex flex-col w-auto">
									<span className="text-xl text-foreground-dark font-semibold">Update Help Topic</span>
								</div>
								<div className="inline-flex flex-row w-auto gap-2">
									<Button
										type="submit"
										variant="ghost"
										disabled={isLoading}
										loading={isLoading}
										onClick={() => {
											window.location.href = "/help-topics";
										}}
									>
										Back
									</Button>

									<Button
										variant="default"
										disabled={isLoading}
										loading={isLoading}
										onClick={() => {
											form.trigger();
											handleSubmit(form.getValues());
										}}
									>
										Submit
									</Button>
								</div>
							</div>
							<div className="flex flex-col w-full">
								<Tabs
									defaultValue="editor"
									onValueChange={() => {
										console.log(form.getValues());
									}}
								>
									<TabsList>
										<TabsTrigger value="editor">Editor</TabsTrigger>
										<TabsTrigger value="preview">Preview</TabsTrigger>
									</TabsList>

									<TabsContent value="editor">
										<div className="my-4">
											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="title"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Title</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Input type="text" autoComplete="off" {...field} disabled={isLoading} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="description"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Description</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Textarea
																		{...field}
																		autoComplete="off"
																		disabled={isLoading}
																		value={field.value ?? ""}
																		maxLength={1000}
																		onChange={field.onChange}
																		onBlur={field.onBlur}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="category"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Categories</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Combobox
																		defaultValue={field.value?.label}
																		onValueChange={(value) => {
																			field.onChange(value);
																		}}
																		clearable={false}
																		placeholderValue={{
																			label: field.value?.label,
																			value: field.value?.value,
																		}}
																		loadOptions={loadOptions}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="roles"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Target Roles</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<TagsInput
																		isMulti={true}
																		defaultValue={field.value}
																		onValueChange={(value) => {
																			field.onChange(value);
																		}}
																		loadOptions={loadRoleOptions}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="video"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Video</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<InputFile
																		{...field}
																		accept="video/mp4, video/webm"
																		onChange={onFileChange}
																		setFile={handleSetFile}
																		removeUploadedFile={handleRemoveFile}
																		uploadedFile={data?.attachment || null}
																		disabled={isLoading}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											{videoFile && videoFile.original_url ? (
												<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
													<div className="col-span-12 w-full">
														<video className="block w-full" disablePictureInPicture playsInline controls={true}>
															<source src={videoFile.original_url} type={videoFile.mime_type ? data.attachment.mime_type : "video/mp4"} />
														</video>
													</div>
												</div>
											) : null}

											{videoFile && (
												<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
													<div className="col-span-12 w-full">
														<video className="block w-full" disablePictureInPicture playsInline controls={true}>
															<source src={videoFile} type={videoFile ? videoFile.type : "video/mp4"} />
														</video>
													</div>
												</div>
											)}

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="body"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Body</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	{/* <Input type="text" autoComplete="off" {...field} /> */}
																	<Tiptap content={field.value} onUpdate={field.onChange} disabled={true} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>
										</div>
									</TabsContent>

									<TabsContent value="preview">
										<div className="w-full h-full flex flex-col gap-6 mt-10">
											<div className="flex justify-center">
												<div className="w-9/12 gap-3 flex flex-col">
													<div className="flex flex-col">
														<div className="text-3xl font-bold">{form.getValues("title")}</div>
													</div>

													<div className="flex flex-col">
														<div className="">{form.getValues("description")}</div>
													</div>

													{videoFile && (
														<div className="flex flex-col">
															<video className="block w-full" disablePictureInPicture playsInline controls={true}>
																<source src={videoFile} type={videoFile ? videoFile.type : "video/mp4"} />
															</video>
														</div>
													)}

													<div className="font-base text-gray-600 text-pretty">
														<div
															className="text-base text-foreground-dark prose editor"
															// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
															dangerouslySetInnerHTML={{
																__html: form.getValues("body") || "",
															}}
															key={Date()}
														/>
													</div>
												</div>
											</div>
										</div>
									</TabsContent>
								</Tabs>
							</div>
						</form>
					</Form>
				</div>
			</div>
		</Content>
	);
}
